"use client"

import { ComingSoon } from "@/app/components/coming-soon"
import { SweepIcon } from "@/app/icons/sweep"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { Trans } from "@lingui/react/macro"
import { InputNumber, Slider } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useSuspenseInfiniteQuery } from "@tanstack/react-query"
import { useMolecule } from "bunshi/react"
import { useCallback, useMemo } from "react"
import { SweepMoleculeReatom } from "../molecule.reatom"
import { SweepSelectorMoleculeReatom } from "./molecule.reatom"

export const SweepSelectorView = reatomComponent(() => {
  const { getMaxSweep, getDisplayCount, setCountAction } = useMolecule(
    SweepSelectorMoleculeReatom,
  )
  const { nftSearchQueryParamsAtom, nftsAvailableToSweep } =
    useMolecule(SweepMoleculeReatom)
  const { unionAddress } = useMolecule(WalletConnectMoleculeReatom)

  // Fetch NFT search results
  const queryParams = nftSearchQueryParamsAtom()
  const { data } = useSuspenseInfiniteQuery(queryParams)

  // Extract all NFTs and filter available ones
  const { allNfts, availableNfts } = useMemo(() => {
    const all = data.pages.flatMap((page) => page.nfts)
    const userWallet = unionAddress()
    const available = nftsAvailableToSweep()(all, userWallet)
    return { allNfts: all, availableNfts: available }
  }, [data, unionAddress, nftsAvailableToSweep])

  // Get display count based on current selection
  const count = getDisplayCount(allNfts)
  const maxSweep = getMaxSweep(availableNfts.length)

  const sliderValue = useMemo(() => [count ?? 0], [count])

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value
      const newCount = inputValue !== "" ? Number(inputValue) : undefined
      setCountAction(newCount, availableNfts)
    },
    [setCountAction, availableNfts],
  )

  const onSliderChange = useCallback(
    ([value]: number[]) => {
      setCountAction(value, availableNfts)
    },
    [setCountAction, availableNfts],
  )

  const countInputValue = (count === 0 ? undefined : count) ?? ""

  return (
    <div className="flex flex-shrink-0 items-center justify-stretch gap-6">
      <InputNumber
        value={countInputValue}
        min={0}
        max={maxSweep}
        onChange={onInputChange}
        className="w-auto shrink-0"
        placeholder="0"
        label={
          <div className="flex items-center gap-[6px] text-foreground">
            <SweepIcon />
            <span className="sr-only @4xl:not-sr-only">
              <Trans>Sweep</Trans>
            </span>
          </div>
        }
        id="sweep-input"
        data-testid="sweep-input"
      />
      <Slider
        className="shrink-0 grow-0 basis-[125px] cursor-pointer"
        value={sliderValue}
        max={maxSweep}
        onValueChange={onSliderChange}
        data-testid="sweep-slider"
      />
    </div>
  )
}, "SweepSelectorView")

// TODO: remove this once the Sweep is implemented
export function SweepSelectorComingSoon() {
  return (
    <div className="flex flex-shrink-0 items-center justify-stretch gap-8">
      <ComingSoon>
        <InputNumber
          value={""}
          min={0}
          max={0}
          onChange={() => {}}
          className="w-auto shrink-0"
          placeholder="0"
          label={
            <div className="flex items-center gap-[6px] text-foreground">
              <SweepIcon />
              <span className="sr-only @4xl:not-sr-only">
                <Trans>Sweep</Trans>
              </span>
            </div>
          }
          id="sweep-input"
          data-testid="sweep-input"
          disabled
        />
      </ComingSoon>
      <ComingSoon>
        <Slider
          className="shrink-0 grow-0 basis-[125px] cursor-pointer"
          value={[0]}
          max={0}
          onValueChange={() => {}}
          data-testid="sweep-slider"
          disabled
        />
      </ComingSoon>
    </div>
  )
}
